import { classNames } from '@components/utils/helper'
import { settingsNavigation } from '@constants/navigation.constant'
import { useEffect, useRef } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

export default function SettingsSideNavigationBar({ Routes }) {
  const location = useLocation()
  const navRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef(null)

  // Easing function for smooth scroll
  const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
    t /= d / 2
    if (t < 1) return (c / 2) * t * t + b
    t--
    return (-c / 2) * (t * (t - 2) - 1) + b
  }

  useEffect(() => {
    const scrollToSmooth = (element, to, duration) => {
      const start = element.scrollLeft
      const change = to - start
      let currentTime = 0
      const increment = 20

      const animateScroll = () => {
        currentTime += increment
        const val = easeInOutQuad(currentTime, start, change, duration)
        element.scrollLeft = val
        if (currentTime < duration) {
          requestAnimationFrame(animateScroll)
        }
      }

      animateScroll()
    }

    // Scroll to center the selected item
    if (navRef.current) {
      const selectedNavItem = navRef.current.querySelector('.selected') as HTMLElement
      if (selectedNavItem) {
        const targetScroll = selectedNavItem.offsetLeft - (navRef.current.offsetWidth - selectedNavItem.offsetWidth) / 2
        scrollToSmooth(navRef.current, targetScroll, 500) // 500ms duration for smooth scrolling
      }
    }
  }, [location.pathname])

  return (
    <>
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <aside
          ref={navRef}
          className="sticky top-0 flex overflow-x-auto border-b border-gray-900/5 py-4 lg:block lg:w-64 lg:flex-none lg:border-0 lg:py-20"
        >
          <nav className="flex-none px-4 sm:px-6 lg:px-0" ref={scrollRef}>
            <ul role="list" className="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col">
              {settingsNavigation?.map((item) => (
                <li key={item.key}>
                  <NavLink
                    to={item.href}
                    className={classNames(
                      item.href === location.pathname
                        ? 'selected bg-gray-50 text-indigo-600'
                        : 'text-darkgray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex gap-x-3 rounded-md py-2 pr-3 pl-2 text-sm leading-6 font-semibold',
                    )}
                  >
                    {item.icon && (
                      <item.icon
                        className={classNames(
                          item.href === location.pathname ? 'text-indigo-600' : 'text-darkgray-400 group-hover:text-indigo-600',
                          'h-6 w-6 shrink-0',
                        )}
                        aria-hidden="true"
                      />
                    )}
                    {item.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="w-full p-4 lg:px-0 lg:py-20">{Routes}</div>
      </div>
    </>
  )
}
